import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatTimeAgo, getInitials, cn } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Lock, Send, Loader2, Clock, Shield } from 'lucide-react';
import VoiceRecorder, { VoiceMessagePlayer } from '../components/chat/VoiceRecorder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecretChatRoomPage() {
  const { chatId } = useParams();
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const fetchChat = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/secret-chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundChat = response.data.find(c => c.id === chatId);
      if (foundChat) {
        setChat(foundChat);
      } else {
        navigate('/secret-chats');
      }
    } catch (error) {
      console.error('Failed to fetch chat:', error);
      navigate('/secret-chats');
    }
  }, [chatId, token, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/secret-chats/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  }, [chatId, token]);

  useEffect(() => {
    fetchChat();
    fetchMessages();
    const interval = setInterval(fetchMessages, 2000);
    return () => clearInterval(interval);
  }, [fetchChat, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getChatName = () => {
    if (!chat) return '';
    const other = chat.participants?.find(p => p.id !== user?.id);
    return other?.display_name || other?.username || 'Unknown';
  };

  const getChatAvatar = () => {
    if (!chat) return '';
    const other = chat.participants?.find(p => p.id !== user?.id);
    return other?.avatar || '';
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/secret-chats/${chatId}/messages`, {
        content: newMessage.trim(),
        message_type: 'text'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleSendVoice = async (audioData, duration) => {
    try {
      const response = await axios.post(`${API}/secret-chats/${chatId}/messages`, {
        content: audioData,
        message_type: 'voice',
        metadata: { duration }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Failed to send voice message:', error);
    }
  };

  const getTimeRemaining = (autoDeleteAt) => {
    const deleteTime = new Date(autoDeleteAt);
    const now = new Date();
    const diffMs = deleteTime - now;
    
    if (diffMs <= 0) return 'Deleted';
    
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}min rete`;
    
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h rete`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen max-w-2xl mx-auto bg-background" data-testid="secret-chat-room">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-primary/10 backdrop-blur-xl border-b border-primary/20 px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/secret-chats')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <div className="relative">
            <Avatar className="w-10 h-10 ring-2 ring-primary/30">
              <AvatarImage src={getChatAvatar()} alt={getChatName()} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {getInitials(getChatName())}
              </AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
              <Lock className="w-2.5 h-2.5" />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold truncate">{getChatName()}</h1>
            <div className="flex items-center gap-1 text-xs text-primary">
              <Shield className="w-3 h-3" />
              <span>Secret Chat · {chat?.auto_delete_minutes}min</span>
            </div>
          </div>
        </div>
      </header>

      {/* Info Banner */}
      <div className="mx-4 mt-4 p-3 bg-primary/5 rounded-2xl border border-primary/10">
        <div className="flex items-center gap-2 text-sm text-primary">
          <Clock className="w-4 h-4" />
          <span>Messages auto-delete after {chat?.auto_delete_minutes} minutes</span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Lock className="w-12 h-12 text-primary/30 mx-auto mb-3" />
              <p className="text-muted-foreground">Send your first secret message!</p>
            </div>
          </div>
        ) : (
          messages.map((message, index) => {
            const isOwn = message.sender_id === user?.id;
            const showAvatar = !isOwn && (index === 0 || messages[index - 1]?.sender_id !== message.sender_id);
            const isVoice = message.message_type === 'voice';
            
            return (
              <div
                key={message.id}
                className={cn("flex gap-2", isOwn && "justify-end")}
                data-testid={`secret-message-${message.id}`}
              >
                {!isOwn && (
                  <div className="w-8">
                    {showAvatar && (
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={message.sender_avatar} />
                        <AvatarFallback className="text-xs bg-primary/10 text-primary">
                          {getInitials(message.sender_username)}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                )}
                
                <div className={cn("max-w-[75%]", isOwn && "order-first")}>
                  <div className={cn(
                    "px-4 py-2 rounded-2xl relative",
                    isOwn 
                      ? "bg-primary text-primary-foreground rounded-br-sm" 
                      : "bg-secondary text-secondary-foreground rounded-bl-sm"
                  )}>
                    {isVoice ? (
                      <VoiceMessagePlayer 
                        audioData={message.content} 
                        duration={message.metadata?.duration} 
                      />
                    ) : (
                      <p className="break-words">{message.content}</p>
                    )}
                  </div>
                  <div className={cn(
                    "flex items-center gap-2 text-xs text-muted-foreground mt-1",
                    isOwn && "justify-end"
                  )}>
                    <span>{formatTimeAgo(message.created_at, language)}</span>
                    <span className="text-primary/70">
                      · {getTimeRemaining(message.auto_delete_at)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="sticky bottom-0 p-4 bg-background border-t border-primary/20 safe-bottom">
        <div className="flex items-center gap-2">
          <VoiceRecorder onSend={handleSendVoice} disabled={sending} />
          <Input
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Secret message..."
            className="h-12 rounded-full bg-primary/5 border-primary/20 focus:border-primary flex-1"
            data-testid="secret-message-input"
          />
          <Button 
            type="submit" 
            size="icon" 
            className="w-12 h-12 rounded-full shrink-0"
            disabled={!newMessage.trim() || sending}
            data-testid="send-secret-message-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
